import {
    AnimationComponent,
    Component,
    MeshComponent,
    Object3D,
    PhysXComponent,
    TextComponent,
    Texture,
} from '@wonderlandengine/api';
import {delay} from '../utils/delay.js';
import {hapticFeedback} from '../utils/haptic-feedback.js';
import {property} from '@wonderlandengine/api/decorators.js';
import {Cursor, CursorTarget} from '@wonderlandengine/components';
import {PhongTexturedMaterial} from '../types/materials.js';
import {InspectWorldObject} from './inspect-world-object.js';
import {CursorReticle, CursorType} from './cursor-reticle.js';
/**
 * crime-doc
 * Add it to CrimeDoc Object to Forward & Backward Pages onCollisions
 * Gets called when an Inspect-World-Object calls it
 */
export class CrimeDoc extends Component {
    static TypeName = 'crime-doc';
    /* Properties that are configurable in the editor */
    @property.object()
    forwardPageCollision?: Object3D;
    @property.object()
    backwardPageCollision?: Object3D;
    @property.object()
    pageAnim?: Object3D;
    @property.object()
    textPageTurn?: Object3D;
    @property.object()
    turnedPage?: Object3D; /* The page that will be activated in the left, to hide the PageTurn Animation */
    @property.object()
    textPage1?: Object3D;
    @property.object()
    texts?: Object3D;
    @property.texture()
    image?: Texture;
    @property.int(0)
    changeImageAfterPage = 0; /* Optional, If 0 no Image will be changed */
    @property.object()
    topImage?: Object3D;
    @property.object()
    player?: Object3D;
    @property.texture()
    newImage?: Texture; /* Optional */

    private pages: TextComponent[] = [];
    private pageNum = 0;
    private textPage1DefaultText = '';
    private gate = false;
    private imageObj?: Object3D;
    private inspectWorldObj?: Object3D;
    private turnedPageMesh?: MeshComponent;
    private textPage1Text?: TextComponent;
    private imageObjMesh?: MeshComponent;
    private cursorReticle?: CursorReticle;
    private pageTurnAnimation?: AnimationComponent;
    private textPageTurnText?: TextComponent;

    start() {
        /* Validate Properties */
        if (!this.forwardPageCollision) {
            throw new Error(
                `crime-doc(${this.object.name}):forwardPageCollision is not defined`
            );
        }
        if (!this.backwardPageCollision) {
            throw new Error(
                `crime-doc(${this.object.name})backwardPageCollision is not defined`
            );
        }
        if (!this.pageAnim) {
            throw new Error(`crime-doc(${this.object.name}):pageAnim is not defined`);
        }
        const tempPageTurnAnimation = this.pageAnim.getComponent(AnimationComponent);
        if (!tempPageTurnAnimation) {
            throw new Error(
                `crime-doc(${this.object.name}):AnimationComponent is missing on pageAnim`
            );
        }
        this.pageTurnAnimation = tempPageTurnAnimation;

        if (!this.textPageTurn) {
            throw new Error(`crime-doc(${this.object.name}):textPageTurn is not defined`);
        }
        const tempTextPageTurnText = this.textPageTurn.getComponent(TextComponent);
        if (!tempTextPageTurnText) {
            throw new Error(
                `crime-doc(${this.object.name}):TextComponent is missing on textPageTurn`
            );
        }
        this.textPageTurnText = tempTextPageTurnText;

        if (!this.turnedPage) {
            throw new Error(`crime-doc(${this.object.name}):turnedPage is not defined`);
        }
        const tempTurnedPageMesh = this.turnedPage.getComponent(MeshComponent);
        if (!tempTurnedPageMesh) {
            throw new Error(
                `crime-doc(${this.object.name}):turnedPageMesh component is not defined on turnedPage`
            );
        }
        this.turnedPageMesh = tempTurnedPageMesh;
        this.turnedPageMesh.active = false;

        if (!this.textPage1) {
            throw new Error(`crime-doc(${this.object.name}):textPage1 is not defined`);
        }
        if (!this.textPage1) {
            throw new Error(`crime-doc(${this.object.name}):textPage1 is not defined`);
        }
        if (!this.texts) {
            throw new Error(`crime-doc(${this.object.name}):texts is not defined`);
        }
        if (!this.topImage) {
            throw new Error(`crime-doc(${this.object.name}):topImage is not defined`);
        }
        if (!this.player) {
            throw new Error(`crime-doc(${this.object.name}):player is not defined`);
        }
        const tempCursorReticle = this.player.getComponent(CursorReticle);
        if (!tempCursorReticle) {
            throw new Error(
                `crime-doc(${this.object.name}):CursorReticle component is not defined on player`
            );
        }
        this.cursorReticle = tempCursorReticle;

        /* Add Cursor Functions */
        /* Page Forward */
        const targetForward =
            this.forwardPageCollision.getComponent(CursorTarget) ||
            this.forwardPageCollision.addComponent(CursorTarget)!;
        targetForward.onHover.add(this.forwardOnHover.bind(this));
        targetForward.onUnhover.add(this.forwardOnUnHover.bind(this));
        targetForward.onDown.add(() => {
            this.forwardOnDown().catch(console.error);
        });
        /* Page Backward */
        const targetBackward =
            this.backwardPageCollision.getComponent(CursorTarget) ||
            this.backwardPageCollision.addComponent(CursorTarget)!;
        targetBackward.onHover.add(this.backwardOnHover.bind(this));
        targetBackward.onUnhover.add(this.backwardOnUnHover.bind(this));
        targetBackward.onDown.add(() => {
            this.backwardOnDown().catch(console.error);
        });

        /* Disable All children's texts except the 1st one */
        for (const child of this.texts.children.sort((a, b) =>
            a.name.localeCompare(b.name)
        )) {
            const childTextComponent = child.getComponent(TextComponent);
            if (!childTextComponent) {
                throw new Error(
                    `crime-doc(${this.object.name}):TextComponent is missing on children of texts.`
                );
            }
            childTextComponent.active = false;
            this.pages.push(childTextComponent);
        }
        this.pages[0].active = true;

        /* General */
        this.pageNum = 1; /* 1 instead of 0 as the first page already active */
        /* Store default text of it to be able to view again when turning back pages */
        const temptextPage1Text = this.textPage1.getComponent(TextComponent);
        if (!temptextPage1Text) {
            throw new Error(
                `crime-doc(${this.object.name}):text component missing on textPage1`
            );
        }
        this.textPage1Text = temptextPage1Text;
        this.textPage1DefaultText = this.textPage1Text.text;
        this.gate = false;

        /* Image */
        const tempImageObjMesh = this.topImage.getComponent(MeshComponent);
        if (!tempImageObjMesh) {
            throw new Error(
                `crime-doc(${this.object.name}):MeshComponent is missing on topImage`
            );
        }
        this.imageObjMesh = tempImageObjMesh;

        this.imageObj = this.topImage;
        if (this.imageObjMesh.active) {
            this.imageObjMesh.material = this.imageObj
                .getComponent(MeshComponent)
                ?.material?.clone(); /* Clone it to make it independent */

            const material = this.imageObjMesh.material as PhongTexturedMaterial;
            material.diffuseTexture = this.image;
        }
    }

    /**
     * Sets the `inspectWorldObj` property and retrieves the `player` property
     * from its `InspectWorldObject` component.
     * @param inspectWorldObj - The `Object3D` to inspect.
     * @throws An error if the `InspectWorldObject` component is missing on `inspectWorldObj`.
     */
    info(inspectWorldObj: Object3D) {
        if (!inspectWorldObj.getComponent(InspectWorldObject)) {
            throw new Error(
                `crime-doc(${this.object.name}):InspectWorldObject component is missing on inspectWorldObj.`
            );
        }
        this.inspectWorldObj = inspectWorldObj;
    }

    /* Forward */
    forwardOnHover(_: Object3D, cursor: Cursor) {
        hapticFeedback(cursor.object, 0.5, 50);

        /* Cursor */
        this.cursorReticle!.changeCursorTexture(cursor, CursorType.NextPage);
    }

    forwardOnUnHover(_: Object3D, cursor: Cursor) {
        hapticFeedback(cursor.object, 0.3, 50);

        /* Cursor */
        this.cursorReticle!.changeCursorTexture(cursor, CursorType.Default);
    }

    async forwardOnDown() {
        if (this.gate) return;

        if (this.pageNum < this.texts!.children.length) {
            this.gate = true;

            /* TurnPage Animation & Text */
            this.pageTurnAnimation!.speed = 1;
            this.pageTurnAnimation!.play();
            this.textPageTurnText!.text = this.textPage1Text!.text; /* previousPageText */
            this.textPage1Text!.text = '';
            await delay(0.2); /* Smoother transition */

            /* Change NextPage Text */
            this.textPage1Text!.text = this.pages[this.pageNum].text;
            /* Change Image after Page */
            if (
                this.changeImageAfterPage != 0 &&
                this.changeImageAfterPage == this.pageNum
            ) {
                (this.imageObjMesh!.material as PhongTexturedMaterial).diffuseTexture =
                    this.newImage;
            }
            await delay(0.8); /* onAnimationEnd */

            /* Left TurnedPage Active */
            this.turnedPageMesh!.active = true;

            ++this.pageNum;
            this.gate = false;
        } else {
            /* If Last Page */
            this.resetSelf();
        }
    }

    /* Backward */
    backwardOnHover(_: Object3D, cursor: Cursor) {
        hapticFeedback(cursor.object, 0.5, 50);

        /* Cursor */
        this.cursorReticle!.changeCursorTexture(cursor, CursorType.PreviousPage);
    }

    backwardOnUnHover(_: Object3D, cursor: Cursor) {
        hapticFeedback(cursor.object, 0.3, 50);

        /* Cursor */
        this.cursorReticle!.changeCursorTexture(cursor, CursorType.Default);
    }

    async backwardOnDown() {
        if (this.gate) return;

        if (this.pageNum > 1) {
            this.gate = true;
            --this.pageNum;

            /* Left TurnedPage Deactive */
            if (this.pageNum === 1) {
                this.turnedPageMesh!.active = false;
            }

            /* TurnPage Animation & Text */
            this.pageTurnAnimation!.speed = -1; /* Reverse Animation */
            this.pageTurnAnimation!.play();
            if (this.pageNum == 1) {
                this.textPageTurnText!.text = this.textPage1DefaultText;
            } else {
                this.textPageTurnText!.text =
                    this.pages[this.pageNum - 1].text; /* previousPageText */
            }
            await delay(0.6); /* onPageTurn */
            /* Change PreviousPage Text */
            if (this.pageNum == 1) {
                this.textPage1Text!.text = this.textPage1DefaultText;
            } else {
                this.textPage1Text!.text = this.pages[this.pageNum - 1].text;
            }
            /* Change Image */
            if (
                this.changeImageAfterPage != 0 &&
                this.changeImageAfterPage == this.pageNum
            ) {
                (this.imageObjMesh!.material as PhongTexturedMaterial).diffuseTexture =
                    this.image;
            }
            await delay(0.27); /* onAnimationEnd */
            this.textPageTurnText!.text = '';

            this.gate = false;
        }
    }

    resetSelf() {
        /* Inventory */
        //this.player.getComponent('inventory').CrimeDocNum = true; //Add to inventory?

        /* Reset InspectWorldObj */

        if (!this.inspectWorldObj) {
            console.error(`newspaper(${this.object.name}):inspectWorldObj is not defined`);
            return this;
        }

        const inspectWorldComponent = this.inspectWorldObj.getComponent(InspectWorldObject);
        if (!inspectWorldComponent) {
            console.error(
                `newspaper(${this.object.name}):InspectWorldObject component is missing on inspectWorldObj.`
            );
            return this;
        }
        inspectWorldComponent.onDown().catch(console.error);

        /* Disable Mesh & Collision of inspecteWorldObj */
        //await delay(0.1); /* Fix a bug where the mesh wasn't getting deactive */
        const inspectWorldObjectMesh = this.inspectWorldObj.getComponent(MeshComponent);
        if (inspectWorldObjectMesh) {
            inspectWorldObjectMesh.active = false;
        }
        if (this.inspectWorldObj.children) {
            for (const child of this.inspectWorldObj.children) {
                const inspectWorldObjectChildMesh = child.getComponent(MeshComponent);
                if (!inspectWorldObjectChildMesh) {
                    console.error(
                        `newspaper(${this.object.name}):Mesh componen is missing on child of inspect world object`
                    );
                } else {
                    inspectWorldObjectChildMesh.active = false;
                }
            }
        }
        const inspectWorldObjectPhysx = this.inspectWorldObj.getComponent(PhysXComponent);
        if (!inspectWorldObjectPhysx) {
            console.error(
                `newspaper(${this.object.name}):PhysXComponent is missing on inspect world object`
            );
        } else {
            inspectWorldObjectPhysx.active = false;
        }
        return this;
    }
}
